import { State } from '@app/store/state';
import { memoize } from 'proxy-memoize';

import { DEFAULT_ACTIVE_WORD_COLOR, DEFAULT_VIDEO_BG_COLOR } from './const';
import {
  AdvancedEditDraft,
  AdvancedEditsState,
  AdvancedEditStatus,
  EditHistory,
  EditPersistCheckpoint,
  RecordingEditEntity,
  RecordingEditListApiRow,
  TranscriptSegment,
} from './domain';
import {
  filterTranscriptSegments,
  getCaptionStyleIdForApiValue,
  getClipPresetBackgroundColor,
  getDefaultPreset,
} from './utils';

export const selectRecording = (state: State) =>
  state.contentHub.recordingInfo.data;

export const selectRecordingPlayback = (state: State) =>
  state.contentHub.recordingInfo.playback;

export const selectRecordingFetchStatus = (state: State) =>
  state.contentHub.recordingInfo.status;

export const selectRecordingBackUrl = (state: State) =>
  state.contentHub.recordingInfo.backUrl;

export const selectRecordingEventInfo = (state: State) =>
  state.contentHub.recordingInfo.eventInfo;

export const selectRecordingEventSpeakers = (state: State) =>
  state.contentHub.recordingInfo.eventSpeakers.data;

export const selectRecordingEventSpeakersStatus = (state: State) =>
  state.contentHub.recordingInfo.eventSpeakers.status;

export const selectRecordingOrgSpeakersCursor = (state: State) =>
  state.contentHub.recordingInfo.eventSpeakers.pagination?.orgSpeakers
    ?.next_cursor;

export const selectRecordingEventSpeakersCursor = (state: State) => ({
  endCursor:
    state.contentHub.recordingInfo.eventSpeakers.pagination?.eventSpeakers
      ?.endCursor || null,
  hasNextPage:
    state.contentHub.recordingInfo.eventSpeakers.pagination?.eventSpeakers
      ?.hasNextPage || false,
});

export const selectRecordingSpeakersSearchText = (state: State) =>
  state.contentHub.recordingInfo.eventSpeakers.searchText;

export const selectRecordingSpeakersStatus = (state: State) =>
  state.contentHub.recordingInfo.eventSpeakers.status;

export const selectRecordingEditorState = (state: State) =>
  state.contentHub.recordingInfo.editorState;

export const selectDraftClips = (state: State) =>
  state.contentHub.recordingInfo.editorState.draft_clips;

export const selectClipDeletionInfo = (state: State) =>
  state.contentHub.recordingInfo.clipDeletionInfo;

export const selectClipExportInfo = (state: State) =>
  state.contentHub.recordingInfo.clipExportInfo;

export const selectClipDiscardAllInfo = (state: State) =>
  state.contentHub.recordingInfo.clipDiscardAllInfo;

export const selectRecordingDescriptionUpdateState = (state: State) =>
  state.contentHub.recordingInfo.descriptionUpdateState;

export const selectSelectedClip = (state: State) =>
  state.contentHub.recordingInfo.editorState.selectedClip;

export const selectRecordingPlaybackCurrentTime = (state: State) =>
  state.contentHub.recordingInfo.playback.currentTime;

export const selectIsPortraitFullscreen = (state: State) =>
  state.contentHub.recordingInfo.playback.isPortraitFullscreen ?? false;

export const selectClipUpdateInfo = (state: State) =>
  state.contentHub.recordingInfo.clipUpdateInfo;

export const selectRecordingCaptionsEnabled = (state: State) =>
  state.contentHub.recordingInfo.captions?.enabled ?? false;

export const selectClipSuggestion = (state: State) =>
  state.contentHub.recordingInfo.editorState.clip_suggestion;

export const selectRecordingTranscriptionStatus = (state: State) =>
  state.contentHub.recordingInfo.data?.transcription_status;

export const selectRecordingTitleEditState = (state: State) =>
  state.contentHub.recordingInfo.titleEditState;

export const selectRecordingTranscript = (state: State) =>
  state.contentHub.recordingInfo.transcript;

export const selectRecordingTranscriptSpeakers = (state: State) =>
  state.contentHub.recordingInfo.transcript.data?.speakers;

export const selectRecordingTranscriptSegments = (state: State) =>
  state.contentHub.recordingInfo.transcript.data?.transcript?.segments;

export const selectSpeakerManagement = (state: State) =>
  state.contentHub.recordingInfo.speakerManagement;

export const selectTranscriptSearchQuery = (state: State) =>
  state.contentHub.recordingInfo.transcriptSearchQuery;

export const selectContextMenuTabId = (state: State) =>
  state.contentHub.recordingInfo.selectedContextMenuTabId;

export const selectAdvancedEditsState = (state: State): AdvancedEditsState =>
  state.contentHub.recordingInfo.advancedEdits;

export const selectActiveAdvancedEdit = (
  state: State,
): RecordingEditEntity | null => {
  const { activeEditId, editEntitiesById } =
    state.contentHub.recordingInfo.advancedEdits;
  if (!activeEditId) return null;
  return editEntitiesById[activeEditId] ?? null;
};

export const selectAdvancedEditList = (
  state: State,
): RecordingEditListApiRow[] =>
  state.contentHub.recordingInfo.advancedEdits.edits;

export const selectAdvancedEditsListStatus = (
  state: State,
): 'idle' | 'loading' | 'success' | 'failed' =>
  state.contentHub.recordingInfo.advancedEdits.listStatus;

export const selectRecordingEditsListCursor = (state: State): string | null =>
  state.contentHub.recordingInfo.advancedEdits.listCursor;

export const selectColorPresets = (state: State) =>
  state.contentHub.recordingInfo.colorPresets.presets;

export const selectColorPresetsStatus = (state: State) =>
  state.contentHub.recordingInfo.colorPresets.status;

// Aspect ratio: for exported clips (cuts) use recording from API; otherwise selected clip in editor
export const selectAspectRatio = (state: State) => {
  const recording = state.contentHub.recordingInfo.data;
  const selectedClip = state.contentHub.recordingInfo.editorState.selectedClip;
  if (recording?.is_original === false) {
    return recording.aspect_ratio ?? '16_9';
  }
  return selectedClip?.aspect_ratio ?? '16_9';
};

// Default values for video background and caption settings
const DEFAULT_VIDEO_BACKGROUND = {
  color: DEFAULT_VIDEO_BG_COLOR,
  blur: false,
};

const DEFAULT_CAPTION_SETTINGS = {
  include_captions: false,
  captions_position: 'bottom' as const,
  captions_style: 'impact-bold',
  highlight_color: DEFAULT_ACTIVE_WORD_COLOR,
  isCustomActiveWordColor: false,
};

// Video background is per-clip; returns the selected clip's video background merged with defaults
// background_color from API: null = blur mode, hex string = solid color, undefined = default
// lastUsedBackgroundColor: frontend-only, preserves color when blur is toggled (lost on reload)
export const selectVideoBackground = memoize((state: State) => {
  const selectedClip = state.contentHub.recordingInfo.editorState.selectedClip;
  const presets = state.contentHub.recordingInfo.colorPresets.presets;
  const apiBackgroundColor = selectedClip?.background_color;
  const lastUsedColor = selectedClip?.lastUsedBackgroundColor;

  // Determine blur mode: API background_color === null means blur
  const blur = apiBackgroundColor === null;

  // Use solid color from API when set; otherwise in blur mode use last-used or preset color (e.g. after reload), then default
  const color =
    typeof apiBackgroundColor === 'string'
      ? apiBackgroundColor
      : lastUsedColor ||
        getClipPresetBackgroundColor(selectedClip?.preset_id, presets) ||
        DEFAULT_VIDEO_BACKGROUND.color;

  return {
    color,
    blur,
  };
});

// Per-clip caption settings; if clip has no include_captions, use global captions.enabled so toggle works before save.
export const selectCaptionSettings = memoize((state: State) => {
  const selectedClip = state.contentHub.recordingInfo.editorState.selectedClip;
  const globalCaptionsEnabled =
    state.contentHub.recordingInfo.captions?.enabled ?? false;
  return {
    include_captions:
      selectedClip?.include_captions ??
      globalCaptionsEnabled ??
      DEFAULT_CAPTION_SETTINGS.include_captions,
    captions_position:
      selectedClip?.captions_position ??
      DEFAULT_CAPTION_SETTINGS.captions_position,
    captions_style:
      getCaptionStyleIdForApiValue(selectedClip?.captions_style) ??
      DEFAULT_CAPTION_SETTINGS.captions_style,
    highlight_color:
      selectedClip?.highlight_color ?? DEFAULT_CAPTION_SETTINGS.highlight_color,
    isCustomActiveWordColor:
      selectedClip?.isCustomActiveWordColor ??
      DEFAULT_CAPTION_SETTINGS.isCustomActiveWordColor,
  };
});

export const selectFilteredTranscriptSegments = memoize(
  (state: State): TranscriptSegment[] => {
    const segments =
      state.contentHub.recordingInfo.transcript.data?.transcript?.segments;
    const speakersMapping =
      state.contentHub.recordingInfo.transcript.data?.speakers;
    const searchQuery = state.contentHub.recordingInfo.transcriptSearchQuery;

    if (!segments) return [];

    return filterTranscriptSegments(segments, searchQuery, speakersMapping);
  },
);

// Advanced Editor History Selectors (Phase 1.1)

/**
 * Get undo/redo history for the active edit.
 * Returns undefined if no active edit.
 */
export const selectActiveEditHistory = (
  state: State,
): EditHistory | undefined => {
  const activeEditId =
    state.contentHub.recordingInfo.advancedEdits.activeEditId;
  if (!activeEditId) return undefined;
  return state.contentHub.recordingInfo.advancedEdits.historyByEditId[
    activeEditId
  ];
};

/**
 * Check if undo is available for the active edit.
 */
export const selectCanUndo = (state: State): boolean => {
  const history = selectActiveEditHistory(state);
  return (history?.undo?.length ?? 0) > 0;
};

/**
 * Check if redo is available for the active edit.
 */
export const selectCanRedo = (state: State): boolean => {
  const history = selectActiveEditHistory(state);
  return (history?.redo?.length ?? 0) > 0;
};

/**
 * Get the current draft for the active edit.
 */
export const selectActiveEditDraft = (
  state: State,
): AdvancedEditDraft | null => {
  const activeEdit = selectActiveAdvancedEdit(state);
  return activeEdit?.draft ?? null;
};

export const selectActiveEditAspectRatio = (state: State) =>
  selectActiveEditDraft(state)?.aspectRatio ?? null;

export const selectActiveEditCaptions = (state: State) =>
  selectActiveEditDraft(state)?.captions ?? { enabled: false as const };

export const selectActiveEditBackgroundColor = (state: State) =>
  selectActiveEditDraft(state)?.backgroundColor ?? null;

/** Brand preset for the active edit (V1: selectedClip.preset_id). */
export const selectActiveEditPresetId = (state: State): string | undefined => {
  const draft = selectActiveEditDraft(state);
  const presets = selectColorPresets(state);
  const defaultPresetId = getDefaultPreset(presets)?.id;
  if (!draft) {
    return defaultPresetId;
  }
  if (draft.captions.enabled) {
    return draft.captions.preset_id;
  }
  return draft.presetId ?? defaultPresetId;
};

/**
 * Get the auto-save persistence checkpoint for an edit (Option 2: Flexible undo).
 * Returns the full checkpoint object with status, version, and saved draft info.
 * @param state Redux state
 * @param editId Optional edit ID; uses active edit if not provided
 * @returns EditPersistCheckpoint object with status and version info
 */
export const selectAdvancedEditPersistState = (
  state: State,
  editId?: string,
): EditPersistCheckpoint => {
  const id =
    editId ?? state.contentHub.recordingInfo.advancedEdits.activeEditId;
  if (!id) {
    return {
      status: 'idle',
      lastSavedVersion: 0,
    };
  }
  return (
    state.contentHub.recordingInfo.advancedEdits.persistByEditId[id] ?? {
      status: 'idle',
      lastSavedVersion: 0,
    }
  );
};

/**
 * Get the current version number for the active edit.
 * Increments on each edit, used to compare against lastSavedVersion.
 */
export const selectActiveEditCurrentVersion = (state: State): number => {
  const activeEditId =
    state.contentHub.recordingInfo.advancedEdits.activeEditId;
  if (!activeEditId) return 0;
  return (
    state.contentHub.recordingInfo.advancedEdits.editVersionByEditId[
      activeEditId
    ] ?? 0
  );
};

/**
 * Get the version number when the active edit was last saved.
 * Compare against selectActiveEditCurrentVersion() to detect unsaved changes.
 */
export const selectActiveEditLastSavedVersion = (state: State): number => {
  const checkpoint = selectAdvancedEditPersistState(state);
  return checkpoint.lastSavedVersion ?? 0;
};

/**
 * Check if the active edit has unsaved changes.
 * Returns true if current version > last saved version.
 */
export const selectActiveEditHasUnsavedChanges = (state: State): boolean => {
  const currentVersion = selectActiveEditCurrentVersion(state);
  const lastSavedVersion = selectActiveEditLastSavedVersion(state);
  return currentVersion > lastSavedVersion;
};

/**
 * Get the current revision number for the active edit.
 * Returns 0 when no edit is active or when the entity is still a placeholder
 * (i.e. fetchRecordingEditById has not yet completed). The saga guards against
 * sending revision: 0 to the PUT endpoint.
 */
export const selectActiveEditRevision = (state: State): number => {
  const activeEdit = selectActiveAdvancedEdit(state);
  return activeEdit?.revision ?? 0;
};

/**
 * Get the word corrections array for the active edit's draft.
 * Returns an empty array when no edit is active or no corrections exist yet.
 */
export const selectActiveEditWordCorrections = (state: State) => {
  const activeEdit = selectActiveAdvancedEdit(state);
  return activeEdit?.draft.wordCorrections ?? [];
};

// ── AI clip suggestions ────────────────────────────────────────────────────────

/** Full AI clip suggestion slice — data + fetch/trigger statuses. */
export const selectAiClipSuggestion = (state: State) =>
  state.contentHub.recordingInfo.aiClipSuggestion;

/**
 * The clips array from the finished suggestion.
 * Empty when no suggestion exists or state is not 'finished'.
 */
export const selectAiClips = (state: State) =>
  state.contentHub.recordingInfo.aiClipSuggestion.data?.clips ?? [];

// ── Edit export ───────────────────────────────────────────────────────────────

/**
 * Returns the export/lifecycle status of the active edit.
 * Null when no edit is active or the entity is still a placeholder.
 */
export const selectActiveEditStatus = (
  state: State,
): AdvancedEditStatus | null => {
  return selectActiveAdvancedEdit(state)?.status ?? null;
};

/**
 * Returns true when the active edit is frozen — i.e. no changes or exports
 * are allowed. This is the case when the edit is PROCESSING (export in
 * progress) or COMPLETED (export finished successfully).
 *
 * Use this as the single gating condition for all edit interactions:
 * title editing, undo/redo, auto-save, timeline cuts, transcript editing,
 * and the export button itself.
 */
export const selectIsEditFrozen = (state: State): boolean => {
  const status = selectActiveEditStatus(state);
  return status === 'PROCESSING' || status === 'COMPLETED';
};

/**
 * Get a comprehensive save indicator for the active edit.
 * Combines save status, unsaved changes, version info, and failure reason
 * for UI display (icons + tooltip).
 */
export const selectActiveEditSaveIndicator = memoize((state: State) => {
  const checkpoint = selectAdvancedEditPersistState(state);
  const currentVersion = selectActiveEditCurrentVersion(state);
  const lastSavedVersion = selectActiveEditLastSavedVersion(state);
  const hasUnsavedChanges = currentVersion > lastSavedVersion;

  // updatedAt comes from the entity (populated by GET and updated by each
  // successful PUT). It reflects the server-confirmed save timestamp shown
  // in the "Saved at <time>" tooltip.
  const activeEdit = selectActiveAdvancedEdit(state);

  return {
    status: checkpoint.status,
    hasUnsavedChanges,
    isSaving: checkpoint.status === 'saving',
    isSaved: checkpoint.status === 'saved' && !hasUnsavedChanges,
    isFailed: checkpoint.status === 'failed',
    currentVersion,
    lastSavedVersion,
    lastSavedAt: checkpoint.lastSavedAt,
    lastSavedDraft: checkpoint.lastSavedDraft,
    /** Server-confirmed ISO timestamp of the last successful save. */
    updatedAt: activeEdit?.updatedAt,
    /** Only present when status === 'failed'. Drives the tooltip error message. */
    failureReason: checkpoint.failureReason,
  };
});
